/**
 * ChoicesAjax — shared choices.js adapter for the Select2 → choices.js migration (APS5-936).
 *
 * choices.js has no built-in remote/AJAX support and no jQuery integration, so the
 * legacy Select2 callsites can't be swapped one-for-one. This component wraps
 * choices.js with the bits the app relied on Select2 for:
 *
 *   - Remote search: debounced $.ajax mirroring Select2's `ajax.{url,type,data,
 *     processResults,delay}` contract (so existing controller endpoints + their
 *     processResults shapers are reused verbatim). Both the flat
 *     `{results:[{id,text,value}]}` and the grouped `{results:[{text,children}]}`
 *     (optgroup) shapes are supported, plus `minimumInputLength` gating.
 *   - A registry keyed off the <select> element so other code can clear / repopulate
 *     / programmatically select without re-querying through jQuery the way Select2
 *     allowed (`$(sel).val(null).trigger('change')`).
 *
 * Uses the global `Choices` (CDN) and the global `jQuery` ($.ajax inherits the app's
 * $.ajaxSetup CSRF header, matching Select2's old request behaviour exactly).
 *
 * Public API (window.ChoicesAjax):
 *   init(elOrSelector, options)      -> Choices instance (or array for class selectors)
 *   get(elOrSelector)                -> first instance bound to a match, or null
 *   clear(elOrSelector)              -> Select2 `.val(null).trigger('change')` equivalent
 *   setChoices(elOrSelector, data, replace) -> rebuild option list (repopulate pattern)
 *   addItem(elOrSelector, value, label)     -> Select2 programmatic `trigger('select')`
 *   destroy(elOrSelector)
 *   auto()                           -> init every [data-choices] (theme parity, runs on DOMContentLoaded)
 *
 * `options` is a Select2-ish superset; unknown Select2 keys are ignored:
 *   placeholder, multiple, allowClear, closeOnSelect, maximumSelectionLength,
 *   minimumResultsForSearch (Infinity -> searchEnabled:false), searchEnabled,
 *   minimumInputLength, shouldSort, removeItemButton, choices ([{id,text}]/[{value,label}]),
 *   renderLabel(item) (override the dropdown/chip label HTML — defaults to item.text),
 *   inputTooShort, loadingText, noResults (notice strings),
 *   onSelect(value,label,el), onRemove(value,el), onChange(el),
 *   ajax: { url, type, dataType, delay, data(params), processResults(resp,params), cache }
 *
 * Paging: an endpoint opts in by returning Select2's `pagination.more` from
 * processResults. The next page is then fetched when the dropdown is scrolled to
 * the bottom and appended, so nothing changes for endpoints that omit the key.
 */
(function (window, $) {
    'use strict';

    if (typeof window.Choices === 'undefined') {
        // Loud so a missing CDN tag during the migration is obvious in the console.
        // eslint-disable-next-line no-console
        console.warn('ChoicesAjax: global Choices (choices.js) is not loaded.');
    }

    function debounce(fn, wait) {
        var t;
        return function () {
            var ctx = this;
            var args = arguments;
            clearTimeout(t);
            t = setTimeout(function () {
                fn.apply(ctx, args);
            }, wait);
        };
    }

    function resolveElements(elOrSelector) {
        if (!elOrSelector) {
            return [];
        }
        if (typeof elOrSelector === 'string') {
            return Array.prototype.slice.call(document.querySelectorAll(elOrSelector));
        }
        if (elOrSelector.jquery) {
            return elOrSelector.toArray();
        }
        if (elOrSelector.nodeType) {
            return [elOrSelector];
        }
        if (elOrSelector.length) {
            return Array.prototype.slice.call(elOrSelector);
        }
        return [];
    }

    /**
     * Translate a Select2 processResults `results` array into a choices.js
     * setChoices payload. Handles both flat items and optgroup (`children`) items.
     *   Select2 flat:  {id, text, disabled?}            -> {value, label, disabled?}
     *   Select2 group: {text, children:[{id,text}]}     -> {label, choices:[{value,label}]}
     */
    function toChoices(results, renderLabel) {
        return (results || []).map(function (item) {
            if (item.children) {
                return {
                    label: item.text,
                    id: item.id,
                    disabled: !!item.disabled,
                    choices: item.children.map(function (c) {
                        return {
                            value: String(c.id),
                            label: renderLabel ? renderLabel(c) : c.text,
                            disabled: !!c.disabled
                        };
                    })
                };
            }
            return {
                value: String(item.id),
                label: renderLabel ? renderLabel(item) : item.text,
                disabled: !!item.disabled
            };
        });
    }

    function notice(label) {
        return [{ value: '', label: label, disabled: true, selected: false }];
    }

    function buildBaseConfig(el, options) {
        var isMultiple = options.multiple || el.multiple;
        var searchEnabled = options.searchEnabled;
        if (searchEnabled === undefined) {
            // Select2's minimumResultsForSearch:Infinity disables the search box.
            searchEnabled = options.minimumResultsForSearch !== Infinity;
        }
        var removeItemButton = options.removeItemButton;
        if (removeItemButton === undefined) {
            removeItemButton = isMultiple || !!options.allowClear;
        }

        var config = {
            allowHTML: true, // badges (Unsubscribed, Admin/Client) are baked into labels
            removeItemButton: removeItemButton,
            searchEnabled: searchEnabled,
            shouldSort: options.shouldSort || false, // preserve server / DOM order by default
            itemSelectText: '',
            placeholderValue: options.placeholder || null,
            placeholder: !!options.placeholder,
            searchPlaceholderValue: options.searchPlaceholder || null
        };

        if (options.maximumSelectionLength || options.maxItemCount) {
            config.maxItemCount = options.maximumSelectionLength || options.maxItemCount;
        }
        if (options.closeOnSelect === false) {
            config.closeDropdownOnSelect = false;
        }
        if (options.choices) {
            // Accept Select2 {id,text} or native choices {value,label}.
            config.choices = options.choices.map(function (c) {
                if (c.value !== undefined || c.label !== undefined) {
                    return c;
                }
                return { value: String(c.id), label: c.text, selected: !!c.selected, disabled: !!c.disabled };
            });
        }
        if (options.pinnedChoices && options.pinnedChoices.length) {
            // Always-present rows (e.g. "Back to Super Admin") shown before any search.
            config.choices = (config.choices || []).concat(options.pinnedChoices.map(function (c) {
                return { value: String(c.value), label: c.label, disabled: !!c.disabled };
            }));
        }
        if (options.ajax) {
            // Remote: never let choices filter locally — the server already filtered,
            // and labels often match on fields (email/phone) not shown in the text.
            config.searchChoices = false;
            config.searchFloor = 0;
            config.searchResultLimit = 100;
        }
        return config;
    }

    /** Distance from the bottom of the dropdown that triggers the next page. */
    var PAGE_SCROLL_THRESHOLD = 32;

    function wireAjax(instance, el, options) {
        var ajax = options.ajax;
        var min = options.minimumInputLength || 0;
        var renderLabel = options.renderLabel;
        // Rows kept at the top of the list in EVERY state (open / typing / results),
        // e.g. the "Back to Super Admin" revert on the impersonation switcher.
        var pinned = (options.pinnedChoices || []).map(function (c) {
            return { value: String(c.value), label: c.label, disabled: !!c.disabled };
        });
        // Paging state for the term currently on screen. `more` only ever goes
        // true for endpoints that report pagination, so unpaged callsites keep
        // their single-request behaviour.
        var state = { term: '', page: 1, more: false, loading: false, seen: {} };

        /** The scrolling element inside the open dropdown (rebuilt on every render). */
        function dropdownList() {
            var outer = instance.containerOuter && instance.containerOuter.element;
            return outer ? outer.querySelector('.choices__list--dropdown .choices__list, .choices__list[aria-expanded] .choices__list') : null;
        }

        /**
         * Append without losing the reader's place. choices.js re-renders the
         * whole list on setChoices, which resets scrollTop to 0 and throws the
         * user back to the first row every time a page lands.
         */
        function appendChoices(choices) {
            var list = dropdownList();
            var top = list ? list.scrollTop : 0;
            var restore = function () {
                var l = dropdownList();
                if (l && top && l.scrollTop !== top) {
                    l.scrollTop = top;
                }
            };
            var done = instance.setChoices(choices, 'value', 'label', false);
            if (done && typeof done.then === 'function') {
                done.then(restore);
            }
            restore();
            // The render can land a frame later than the promise resolves.
            window.requestAnimationFrame(restore);
        }

        function withPinned(list) {
            return pinned.length ? pinned.concat(list) : list;
        }
        function shortNotice() {
            return notice(options.inputTooShort || 'Please enter ' + min + ' or more characters');
        }

        /** Drop rows already on screen. Pages can overlap when rows shift server-side. */
        function dedupe(choices) {
            return choices.filter(function (c) {
                if (!c.value || state.seen[c.value]) {
                    return !c.value; // keep optgroups / notices, which carry no value
                }
                state.seen[c.value] = true;
                return true;
            });
        }

        function fetchResults(term, page) {
            term = term || '';
            page = page || 1;
            if (term.length < min) {
                instance.setChoices(withPinned(shortNotice()), 'value', 'label', true);
                return;
            }

            var append = page > 1;
            if (!append) {
                state.seen = {};
                instance.setChoices(withPinned(notice(options.loadingText || 'Searching…')), 'value', 'label', true);
            }
            state.term = term;
            state.page = page;
            state.loading = true;

            var params = { term: term, page: page };
            var data = ajax.data ? ajax.data(params) : { searchTerm: term };

            $.ajax({
                url: ajax.url,
                type: ajax.type || 'GET',
                dataType: ajax.dataType || 'json',
                data: data,
                cache: ajax.cache || false
            }).done(function (resp) {
                // A newer search started while this page was in flight. Its
                // results would land under the wrong term, so drop them.
                if (state.term !== term || state.page !== page) {
                    return;
                }
                var processed = ajax.processResults ? ajax.processResults(resp, params) : { results: resp };
                state.more = !!(processed.pagination && processed.pagination.more);
                var choices = dedupe(toChoices(processed.results, renderLabel));
                if (!choices.length) {
                    if (append) {
                        state.loading = false;
                        return; // nothing new to append, keep what is on screen
                    }
                    choices = notice(options.noResults || 'No results found');
                }
                if (append) {
                    appendChoices(choices);
                } else {
                    instance.setChoices(withPinned(choices), 'value', 'label', true);
                }
                state.loading = false;
            }).fail(function () {
                state.more = false;
                state.loading = false;
                if (!append) {
                    instance.setChoices(withPinned(notice(options.noResults || 'No results found')), 'value', 'label', true);
                }
            });
        }

        var debounced = debounce(function (term) {
            fetchResults(term, 1);
        }, ajax.delay || 250);

        el.addEventListener('search', function (e) {
            state.more = false; // stale until the new term's first page answers
            debounced(e.detail.value);
        });

        el.addEventListener('showDropdown', function () {
            var input = instance.input && instance.input.value;
            if (input) {
                return;
            }
            state.more = false;
            if (min === 0) {
                fetchResults('', 1); // minimumInputLength:0 → load the remote "recent" list
            } else {
                // Show the pinned row(s) + "begin typing" hint instead of choices.js's
                // bare "No choices to choose from" when the field opens empty.
                instance.setChoices(withPinned(shortNotice()), 'value', 'label', true);
            }
        });

        // Infinite scroll. Bound in the capture phase on the outer container
        // because scroll does not bubble and choices.js rebuilds the inner
        // dropdown list element on every setChoices.
        var outer = instance.containerOuter && instance.containerOuter.element;
        if (outer) {
            outer.addEventListener('scroll', function (e) {
                var list = e.target;
                if (!list || !list.classList || state.loading || !state.more) {
                    return;
                }
                if (list.scrollTop + list.clientHeight >= list.scrollHeight - PAGE_SCROLL_THRESHOLD) {
                    fetchResults(state.term, state.page + 1);
                }
            }, true);
        }
    }

    function bindCallbacks(instance, el, options) {
        if (options.onSelect) {
            el.addEventListener('addItem', function (e) {
                options.onSelect(e.detail.value, e.detail.label, el);
            });
        }
        if (options.onRemove) {
            el.addEventListener('removeItem', function (e) {
                options.onRemove(e.detail.value, el);
            });
        }
        if (options.onChange) {
            el.addEventListener('change', function () {
                options.onChange(el);
            });
        }
    }

    function initOne(el, options) {
        options = options || {};
        if (el.choicesInstance) {
            // Idempotent re-init (modal reopen, share-modal destroy+init).
            try {
                el.choicesInstance.destroy();
            } catch (err) { /* already gone */ }
            el.choicesInstance = null;
        }

        var instance = new window.Choices(el, buildBaseConfig(el, options));
        el.choicesInstance = instance;

        if (options.ajax) {
            wireAjax(instance, el, options);
        }
        bindCallbacks(instance, el, options);
        return instance;
    }

    var ChoicesAjax = {
        init: function (elOrSelector, options) {
            var els = resolveElements(elOrSelector);
            var instances = els.map(function (el) {
                return initOne(el, options);
            });
            return instances.length === 1 ? instances[0] : instances;
        },

        get: function (elOrSelector) {
            var els = resolveElements(elOrSelector);
            for (var i = 0; i < els.length; i++) {
                if (els[i].choicesInstance) {
                    return els[i].choicesInstance;
                }
            }
            return null;
        },

        getAll: function (elOrSelector) {
            return resolveElements(elOrSelector)
                .map(function (el) { return el.choicesInstance; })
                .filter(Boolean);
        },

        /**
         * Select2 `.val(null).trigger('change')` — drop all selections, fire change.
         * Only deselects (removeActiveItems); it deliberately does NOT clearChoices,
         * so static-DOM selects keep their option list (remote selects refresh their
         * list on the next open/search anyway).
         */
        clear: function (elOrSelector) {
            ChoicesAjax.getAll(elOrSelector).forEach(function (instance) {
                instance.removeActiveItems();
                $(instance.passedElement.element).trigger('change');
            });
        },

        /** Rebuild the option list (repopulate pattern). data = Select2 results array. */
        setChoices: function (elOrSelector, data, replace) {
            var instance = ChoicesAjax.get(elOrSelector);
            if (!instance) {
                return;
            }
            instance.setChoices(toChoices(data), 'value', 'label', replace !== false);
        },

        /** Select2 programmatic `.select2('trigger','select',{data})` — add + select an item. */
        addItem: function (elOrSelector, value, label) {
            var instance = ChoicesAjax.get(elOrSelector);
            if (!instance) {
                return;
            }
            value = String(value);
            // Skip if already selected — appendToPicklistId / role-load loops can re-run
            // and choices.js would otherwise render a duplicate chip for the same value.
            var current = $(instance.passedElement.element).val();
            var selected = Array.isArray(current) ? current : (current ? [current] : []);
            if (selected.indexOf(value) !== -1) {
                return;
            }
            instance.setChoices(
                [{ value: value, label: label, selected: true }],
                'value', 'label', false
            );
            $(instance.passedElement.element).trigger('change');
        },

        destroy: function (elOrSelector) {
            resolveElements(elOrSelector).forEach(function (el) {
                if (el.choicesInstance) {
                    try {
                        el.choicesInstance.destroy();
                    } catch (err) { /* noop */ }
                    el.choicesInstance = null;
                }
            });
        },

        /** Theme-parity declarative init for `[data-choices]` simple selects. */
        auto: function () {
            document.querySelectorAll('[data-choices]').forEach(function (el) {
                if (el.choicesInstance) {
                    return;
                }
                initOne(el, {
                    searchEnabled: el.hasAttribute('data-choices-search-true'),
                    removeItemButton: el.hasAttribute('data-choices-removeItem') || el.hasAttribute('data-choices-multiple-remove'),
                    shouldSort: !el.hasAttribute('data-choices-sorting-false'),
                    maximumSelectionLength: el.getAttribute('data-choices-limit') || undefined,
                    placeholder: el.getAttribute('data-choices-placeholder') || undefined
                });
            });
        }
    };

    window.ChoicesAjax = ChoicesAjax;

    document.addEventListener('DOMContentLoaded', function () {
        ChoicesAjax.auto();
    });
})(window, window.jQuery);
